import { ImpersonationService } from "@/services/users/ImpersonationService";
import { UserService } from "@/services/users/UserService";
import { activityService } from "@/services/analytics/ActivityService";

interface OpenSuiteAppParams {
  /** Suite product word, e.g. "Mail" — used for activity logging */
  productWord: string;
  /** Canonical external app URL to open */
  appUrl: string;
  /** Where the click originated (e.g. "splash", "sidebar") — for analytics */
  source: string;
  /** Path on the external app that accepts the SSO token (appended to appUrl) */
  ssoEntryPath?: string;
}

/**
 * Resolve the entry URL for a Suite app: the SSO entry path when one exists,
 * otherwise the canonical app URL.
 */
export const resolveSuiteEntryUrl = (appUrl: string, ssoEntryPath?: string): string =>
  ssoEntryPath ? `${appUrl.replace(/\/$/, "")}${ssoEntryPath}` : appUrl;

/**
 * Mint the SSO-tokened URL for a Suite app. Falls back to the plain entry URL
 * when token generation fails, so the user still gets through to a login page.
 */
export const mintSuiteSsoUrl = async (appUrl: string, ssoEntryPath?: string): Promise<string> => {
  const base = resolveSuiteEntryUrl(appUrl, ssoEntryPath);
  try {
    const imp = ImpersonationService.getImpersonationState();
    const token = await UserService.generateSsoToken(
      imp
        ? { impersonatedUserId: imp.impersonatedUserId, adminUserId: imp.adminUserId }
        : undefined,
    );
    return `${base}?${token}`;
  } catch {
    return base;
  }
};

/**
 * Mint an SSO URL that lands the user on a specific in-app page after login,
 * rather than the app's default landing page.
 *
 * The SSO token is only ever consumed at the app's login entry point — there
 * is no middleware that accepts it on arbitrary routes — so the deep path
 * can't be substituted as the mint target. Instead it rides alongside the
 * token as `next`, which the app's login page reads once the session is
 * live and navigates to.
 */
export const mintSuiteDeepLinkUrl = async (
  appUrl: string,
  deepPath: string,
  ssoEntryPath?: string,
): Promise<string> => {
  const ssoUrl = await mintSuiteSsoUrl(appUrl, ssoEntryPath);
  return `${ssoUrl}&next=${encodeURIComponent(deepPath)}`;
};

/**
 * Open a Suite companion app in a new tab with an SSO token.
 *
 * iOS Safari (iPad/iPhone) silently blocks `window.open()` that runs AFTER an
 * `await`: the first async hop consumes the user-gesture context, so the popup
 * never appears — which is why "See what is possible" never worked on iPad.
 *
 * The fix: open a blank tab SYNCHRONOUSLY inside the click handler (still within
 * the gesture), then redirect that tab once the SSO token resolves. If the
 * browser blocked the popup anyway (`win === null`), fall back to navigating the
 * current tab so the user still gets through.
 */
export const openSuiteApp = async ({
  productWord,
  appUrl,
  source,
  ssoEntryPath,
}: OpenSuiteAppParams): Promise<void> => {
  activityService.logSuiteAppVisit(productWord, appUrl, source);

  // Open synchronously to preserve the user gesture for iOS Safari.
  const win = window.open("about:blank", "_blank");
  if (win) {
    // about:blank is same-origin until we navigate, so we can sever opener.
    try {
      win.opener = null;
    } catch {
      /* cross-origin guard — safe to ignore */
    }
  }

  const url = await mintSuiteSsoUrl(appUrl, ssoEntryPath);
  if (win) win.location.href = url;
  else window.location.href = url; // popup blocked — fall back to same tab
};
